/**
 * Idioma y formato, disponibles en todo el árbol.
 *
 * El orden de resolución del idioma está en `resolveLanguage`
 * (`i18n/languages.ts`), que es una función pura y tiene sus propios tests:
 * persona → marca → navegador → español.
 *
 * El **locale de formato** no se elige: es el del tenant, siempre. Ver
 * `tenant/settings.ts`.
 *
 * Este provider hace además dos cosas que ninguna vista debería repetir:
 *
 * 1. **Persiste** la elección de idioma, para que sobreviva a la recarga.
 * 2. **Sincroniza `<html lang>`.** Sin eso el documento sigue anunciando
 *    `lang="es"` con la interfaz en portugués: un lector de pantalla lo lee
 *    con fonética española y el navegador ofrece traducir una página que ya
 *    está en el idioma de quien la mira.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { useAuth } from '@/auth/useAuth';
import { useTenantContext } from '@/tenant/useTenantSettings';
import { createFormatters } from './format';
import { browserLanguage, isLanguage, resolveLanguage, type Language } from './languages';
import { translator } from './translate';
import { I18nContext, type I18nValue } from './useI18n';

const LANGUAGE_KEY = 'xyvona.dashboard.language.v1';

function storedLanguage(): Language | null {
  try {
    const raw = localStorage.getItem(LANGUAGE_KEY);
    return isLanguage(raw) ? raw : null;
  } catch {
    // Safari en modo privado tira al leer. Sin persistencia, pero con app.
    return null;
  }
}

export function I18nProvider({ children }: { children: ReactNode }): ReactNode {
  const { settings, isResolved } = useTenantContext();
  const { isAuthenticated } = useAuth();
  const [override, setOverride] = useState<Language | null>(storedLanguage);

  const language = resolveLanguage({
    override,
    // Sólo cuenta como "idioma de la marca" cuando de verdad se sabe cuál es
    // la marca: con sesión abierta y su configuración ya traída.
    tenant: isAuthenticated && isResolved ? settings.language : null,
    browser: browserLanguage(),
  });

  const setLanguage = useCallback((next: Language) => {
    setOverride(next);
    try {
      localStorage.setItem(LANGUAGE_KEY, next);
    } catch {
      // Sin persistencia el selector sigue funcionando para esta sesión.
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo<I18nValue>(
    () => ({
      language,
      setLanguage,
      t: translator(language),
      fmt: createFormatters(settings.locale, settings.timeZone, settings.currency),
    }),
    [language, setLanguage, settings.locale, settings.timeZone, settings.currency],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
